import { Tab } from "@headlessui/react";
import classNames from "classnames";
import { ButtonVariant, CoButton } from "components/CoButton";
import { EmptyModal } from "components/Modal";
import Tooltip from "components/Tooltip";
import { SearchMode } from "helpers/api";
import { CitationTypeRisOmitted, ICite } from "helpers/cite";
import copyTextWithFormat from "helpers/copyTextWithFormat";
import { MixpanelCiteType } from "helpers/services/mixpanel/events";
import useAnalytics from "hooks/useAnalytics";
import useLabels from "hooks/useLabels";
import { ReactNode, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { getCookie, setCookie } from "react-use-cookie";

type CiteModalProps = {
  open?: boolean;
  title: string;
  onClose: () => void;
  panelClassname?: string;
  onDownloadRis?: () => void;
  onCopyText?: (selectedTab: CitationTypeRisOmitted) => void;
  citeEventOverride?: (
    citeType: MixpanelCiteType,
    searchMode?: SearchMode
  ) => void; // TODO(Alan): Remove this once events follow feature event pattern like threads
  searchMode?: SearchMode;
} & ICite;

/**
 * @component CiteModal
 * @description Component for citing papers
 * @example
 * return (
 *   <CiteModal
 *    open={true}
 *    onClick={fn}
 *    title="Share"
 *    apa="apa"
 *    bibtex="bibtex"
 *    mla="mla"
 *    havard="havard"
 *    onDownloadRis
 *    onCopyText
 *   />
 * )
 */
function CiteModal({
  onClose,
  open,
  title,
  apa,
  bibtex,
  chicago,
  mla,
  harvard,
  numeric,
  panelClassname,
  onDownloadRis,
  onCopyText,
  citeEventOverride,
  searchMode,
}: CiteModalProps) {
  const [shareLabels, generalLabels] = useLabels("share", "general");

  const refContent = useRef<HTMLDivElement>(null);

  const { citeEvent } = useAnalytics();

  const tabs: {
    key: CitationTypeRisOmitted;
    title: string;
    type: CitationTypeRisOmitted;
    content: string | ReactNode;
  }[] = [];
  if (apa) {
    tabs.push({
      key: "apa" as CitationTypeRisOmitted,
      title: "APA",
      content: apa,
      type: "apa",
    });
  }
  if (mla) {
    tabs.push({
      key: "mla" as CitationTypeRisOmitted,
      title: "MLA",
      content: mla,
      type: "mla",
    });
  }
  if (chicago) {
    tabs.push({
      key: "chicago" as CitationTypeRisOmitted,
      title: "Chicago",
      content: chicago,
      type: "chicago",
    });
  }
  if (harvard) {
    tabs.push({
      key: "harvard" as CitationTypeRisOmitted,
      title: "Harvard",
      content: harvard,
      type: "harvard",
    });
  }
  if (bibtex) {
    tabs.push({
      key: "bibtex" as CitationTypeRisOmitted,
      title: "BibTeX",
      content: bibtex,
      type: "bibtex",
    });
  }
  if (numeric) {
    tabs.push({
      key: "numeric" as CitationTypeRisOmitted,
      title: "AMA/Numeric",
      content: numeric,
      type: "numeric",
    });
  }

  const [activeType, setActiveType] = useState<CitationTypeRisOmitted>("apa");

  useEffect(() => {
    const selectedTab = getCookie(
      "lastSelectedCitation"
    ) as CitationTypeRisOmitted;
    if (selectedTab) {
      setActiveType(selectedTab);
    }
  }, []);

  const handleCiteEvent = (citeType: MixpanelCiteType) => {
    if (citeEventOverride) {
      citeEventOverride(citeType, searchMode);
    } else {
      citeEvent(citeType);
    }
  };

  const handleClickCopyText = () => {
    const selectedTab = tabs.find((tabItem) => tabItem.key === activeType);
    if (onCopyText) {
      onCopyText(activeType);
      return;
    }
    let htmlText = refContent.current?.outerHTML;
    if (htmlText) {
      toast.success(shareLabels["copy-message"]);
      copyTextWithFormat(htmlText);
      handleCiteEvent(selectedTab!.type);
    }
  };

  const handleDownloadRisClick = () => {
    if (onDownloadRis) {
      onDownloadRis();
      handleCiteEvent("ris");
    }
  };

  const handleSelectTab = (index: number) => {
    const selectedTab = tabs[index].key;
    setCookie("lastSelectedCitation", selectedTab);
    setActiveType(selectedTab);
  };

  const selectedIndex = tabs.findIndex((tab) => tab.key === activeType) ?? 0;

  return (
    <EmptyModal open={open} onClose={onClose}>
      <div className="bg-bg-base rounded-t-xl w-full sm:rounded-b-xl sm:w-[500px] max-h-[95vh] border-box sm:max-h-[600px] overflow-hidden flex flex-col justify-items-stretch items-stretch border sm:border-0">
        <div className="pt-3 pl-4 pr-1.5">
          <div className="flex items-stretch">
            <h1 className="flex items-center flex-1 lg-bold">{title}</h1>
            <div className="flex gap-2">
              {onDownloadRis && (
                <Tooltip
                  content={generalLabels["export-ris"]}
                  placement="bottom"
                >
                  <CoButton
                    variant={ButtonVariant.Flat}
                    size="sm"
                    icon="icon-download"
                    onClick={handleDownloadRisClick}
                    aria-label={generalLabels["export-ris"]}
                  />
                </Tooltip>
              )}
              <CoButton
                variant={ButtonVariant.Flat}
                size="sm"
                onClick={onClose}
                icon="icon-x"
                data-testid="close-button"
              />
            </div>
          </div>
        </div>
        <Tab.Group selectedIndex={selectedIndex} onChange={handleSelectTab}>
          <Tab.List className="flex px-4 border-b">
            {tabs.map((tab) => (
              <Tab
                key={tab.key}
                className={({ selected }) =>
                  classNames(
                    "py-4 mr-4",
                    selected
                      ? "border-b-2 border-accent-border-base text-fg-base sm-md"
                      : "text-fg-muted sm-normal"
                  )
                }
              >
                {tab.title}
              </Tab>
            ))}
          </Tab.List>
          <Tab.Panels>
            {tabs.map((tab) => (
              <Tab.Panel
                key={tab.key}
                className={classNames(
                  "min-h-[260px] text-fg-base p-4",
                  panelClassname
                )}
              >
                {typeof tab.content === "string" ? (
                  <div
                    ref={refContent}
                    dangerouslySetInnerHTML={{ __html: tab.content }}
                  />
                ) : (
                  <div ref={refContent}>{tab.content}</div>
                )}
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>

        <div className="flex flex-col-reverse gap-2 p-4 sm:flex-row sm:justify-end sticky bottom-0 bg-bg-base border-t">
          <CoButton
            variant={ButtonVariant.Tertiary}
            size="sm"
            onClick={onClose}
            data-testid="cancel-button"
            label={generalLabels["cancel"]}
          />
          <CoButton
            variant={ButtonVariant.Primary}
            size="sm"
            icon="icon-clipboard"
            onClick={handleClickCopyText}
            label={shareLabels["copy-text"]}
          />
        </div>
      </div>
    </EmptyModal>
  );
}

export default CiteModal;
